import { useEffect, useState } from "react";
import { ChevronLeft, ChevronRight, Maximize2, X } from "lucide-react";
import { useI18n } from "@/context/I18nContext";

export interface GalleryPhoto {
  src: string;
  alt: string;
}

interface EventPhotoGalleryProps {
  photos: GalleryPhoto[];
}

export function EventPhotoGallery({ photos }: EventPhotoGalleryProps) {
  const { t } = useI18n();
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const selectedPhoto = activeIndex === null ? undefined : photos[activeIndex];

  useEffect(() => {
    if (activeIndex === null || photos.length === 0) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setActiveIndex(null);
      if (event.key === "ArrowLeft") {
        setActiveIndex((current) =>
          current === null ? null : (current - 1 + photos.length) % photos.length,
        );
      }
      if (event.key === "ArrowRight") {
        setActiveIndex((current) => (current === null ? null : (current + 1) % photos.length));
      }
    };
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [activeIndex, photos.length]);

  if (photos.length === 0) return null;

  return (
    <>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {photos.map((photo, index) => (
          <figure
            key={`${photo.src}-${index}`}
            className="overflow-hidden rounded-2xl border border-border/80 bg-background shadow-sm"
          >
            <button
              type="button"
              onClick={() => setActiveIndex(index)}
              aria-label={`${t("productDetail.gallery.zoomAriaLabel")}: ${photo.alt}`}
              title={t("productDetail.gallery.zoomTitle")}
              className="group relative block w-full cursor-zoom-in overflow-hidden focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
            >
              <img
                src={photo.src}
                alt={photo.alt}
                loading="lazy"
                decoding="async"
                className="h-56 w-full object-cover transition duration-300 group-hover:scale-[1.03]"
              />
              <span className="absolute right-3 top-3 rounded-full bg-navy/75 p-2 text-white opacity-0 shadow-lg backdrop-blur-sm transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100">
                <Maximize2 className="h-4 w-4" aria-hidden="true" />
              </span>
            </button>
            <figcaption className="p-3 text-xs font-semibold text-navy/80">{photo.alt}</figcaption>
          </figure>
        ))}
      </div>

      {selectedPhoto && activeIndex !== null && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label={selectedPhoto.alt}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 p-4 backdrop-blur-xl animate-in fade-in duration-200 md:p-8"
          onClick={() => setActiveIndex(null)}
        >
          <button
            type="button"
            autoFocus
            onClick={() => setActiveIndex(null)}
            className="absolute right-4 top-4 z-10 rounded-full bg-white/20 p-3 text-white shadow-2xl transition-colors hover:bg-white hover:text-black focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white md:right-6 md:top-6"
            aria-label={t("productDetail.lightbox.closeAriaLabel")}
          >
            <X className="h-6 w-6" aria-hidden="true" />
          </button>

          <div
            className="relative flex max-h-[92vh] max-w-[92vw] flex-col items-center justify-center"
            onClick={(event) => event.stopPropagation()}
          >
            <img
              src={selectedPhoto.src}
              alt={selectedPhoto.alt}
              className="max-h-[78vh] max-w-[90vw] rounded-xl object-contain drop-shadow-2xl"
            />

            {photos.length > 1 && (
              <>
                <button
                  type="button"
                  onClick={() => setActiveIndex((activeIndex - 1 + photos.length) % photos.length)}
                  className="absolute left-2 top-1/2 -translate-y-1/2 rounded-full bg-black/45 p-3 text-white transition-colors hover:bg-white hover:text-black focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white md:-left-16"
                  aria-label={t("productDetail.gallery.previousImage")}
                >
                  <ChevronLeft className="h-6 w-6" aria-hidden="true" />
                </button>
                <button
                  type="button"
                  onClick={() => setActiveIndex((activeIndex + 1) % photos.length)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full bg-black/45 p-3 text-white transition-colors hover:bg-white hover:text-black focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white md:-right-16"
                  aria-label={t("productDetail.gallery.nextImage")}
                >
                  <ChevronRight className="h-6 w-6" aria-hidden="true" />
                </button>
              </>
            )}

            <div className="mt-4 max-w-2xl text-center text-sm font-semibold text-white/90">
              <p>{selectedPhoto.alt}</p>
              <p className="mt-1 font-mono text-xs text-white/65">
                {activeIndex + 1} {t("productDetail.lightbox.of")} {photos.length}
              </p>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
